/**
 * ONE Suspended status for bakers and partners, derived once
 * (docs/finance/onboarding.html#suspension).
 *
 * An organization is Suspended only while its Stripe onboarding is unfinished
 * or its VAT status is not stated - and when an operator has pressed Suspend
 * in Internal. Nothing else suspends: an incomplete legal identity, new bank
 * details after a failed payout, and a requirement Stripe raises AFTER the
 * account was verified (founder, 27 September 2026, issue #1627) all hold only
 * the payout, on Pending payouts, and the orders keep coming.
 *
 * What the tag does is the readers' business, and there are two: a suspended
 * baker is not sellable (packages/allocation, `commerciallyEligible`), and a
 * suspended partner's QR codes take no order (the guest gate). Suspension never
 * blocks signing up, adding apartments or generating QR codes.
 *
 * It replaced `houseTradingState` (SUSPENDED / RESTRICTED / INCOMPLETE /
 * ACTIVE), which spoke for bakers only and read the payout state as the
 * house's standing - so a verified house whose account Stripe later
 * restricted stopped selling, which is exactly what the founder ruled out.
 */
import type { PayoutVerificationState, SuspensionReason, VatStatus } from '../enums';

/** Why an organization is suspended, in the order a screen names them. */
export const SUSPENSION_REASONS: readonly SuspensionReason[] = [
  'MANUAL',
  'ONBOARDING',
  'VAT_STATUS',
];

/** The one word an organization's standing prints. */
export type OrganizationStanding = 'SUSPENDED' | 'ACTIVE';

export const ORGANIZATION_STANDINGS: readonly OrganizationStanding[] = ['SUSPENDED', 'ACTIVE'];

/** Whether the payout onboarding has ever finished. */
export interface PayoutOnboardingCondition {
  readonly payoutVerification: PayoutVerificationState;
  /** True once the account has been VERIFIED at least once; never cleared. */
  readonly payoutOnboarded: boolean;
}

/**
 * The onboarding is finished once the account has been verified - now, or at
 * any time before. A requirement raised after that is a payout hold, not an
 * unfinished onboarding.
 */
export function payoutOnboardingFinished(org: PayoutOnboardingCondition): boolean {
  return org.payoutOnboarded || org.payoutVerification === 'VERIFIED';
}

/** Everything the standing is decided from. */
export interface StandingCondition extends PayoutOnboardingCondition {
  /** The operator's own switch: false is a manual Suspend in Internal. */
  readonly active: boolean;
  readonly vatStatus: VatStatus | null;
}

/** Every reason the organization is suspended; empty when it is not. */
export function suspensionReasons(org: StandingCondition): readonly SuspensionReason[] {
  const reasons: SuspensionReason[] = [];
  if (!org.active) reasons.push('MANUAL');
  if (!payoutOnboardingFinished(org)) reasons.push('ONBOARDING');
  if (org.vatStatus === null) reasons.push('VAT_STATUS');
  return reasons;
}

/** The one word, off the reasons a record already carries. */
export function organizationStanding(org: {
  readonly suspendedFor: readonly SuspensionReason[];
}): OrganizationStanding {
  return org.suspendedFor.length > 0 ? 'SUSPENDED' : 'ACTIVE';
}

/**
 * A requirement Stripe raised AFTER verification: the onboarding finished, and
 * the account no longer takes transfers (or is still reviewing one). It holds
 * the payout - Pending payouts marks the row - and suspends nothing.
 */
export function payoutHeldAfterVerification(org: PayoutOnboardingCondition): boolean {
  return org.payoutOnboarded && org.payoutVerification !== 'VERIFIED';
}
